import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import api, { inr } from "../../lib/api";

const COLORS = ["#0757D9", "#12C8F5", "#071B3A", "#22C55E", "#F59E0B", "#8B5CF6", "#EF4444", "#64748B", "#0EA5E9", "#14B8A6"];

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/customer").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const k = data.kpis;
  const series = data.series.labels.map((l, i) => ({ day: l, shipments: data.series.volume[i], spend: data.series.spend[i] }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Analytics</h1>
        <p className="text-sm text-slate-500">Last 30 days of shipping performance.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {[
          ["Shipments", k.total, false], ["Spend", inr(k.spend), true], ["Delivery Success", k.delivery_success + "%", true],
          ["NDR Rate", k.ndr_rate + "%", true], ["RTO Rate", k.rto_rate + "%", true],
        ].map(([label, val]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`analytics-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
            <p className="mono mt-2 text-xl font-bold text-shipany-navy">{val}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="analytics-volume-chart">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Shipment Volume</h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs><linearGradient id="vol" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0757D9" stopOpacity={0.35} /><stop offset="100%" stopColor="#0757D9" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={6} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="shipments" stroke="#0757D9" strokeWidth={2} fill="url(#vol)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="analytics-spend-chart">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Daily Spend</h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs><linearGradient id="spd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#12C8F5" stopOpacity={0.4} /><stop offset="100%" stopColor="#12C8F5" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={6} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <Tooltip formatter={(v) => inr(v)} />
                <Area type="monotone" dataKey="spend" stroke="#12C8F5" strokeWidth={2} fill="url(#spd)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="analytics-courier-split">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Courier Split</h2>
          <div className="mt-2 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.courier_split} dataKey="count" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {data.courier_split.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="analytics-pincode-perf">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Pincode Performance (highest NDR first)</h2>
          <table className="mt-3 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-2">Pincode</th><th>Shipments</th><th>NDR</th><th>RTO</th><th>NDR %</th></tr></thead>
            <tbody>
              {data.pincode_performance.map((p) => (
                <tr key={p.pincode} className="border-t border-slate-100">
                  <td className="mono py-2.5 font-semibold text-shipany-navy">{p.pincode}</td>
                  <td className="mono">{p.total}</td>
                  <td className="mono text-orange-600">{p.ndr}</td>
                  <td className="mono text-red-500">{p.rto}</td>
                  <td className={`mono font-bold ${p.ndr_rate > 20 ? "text-red-600" : "text-emerald-600"}`}>{p.ndr_rate}%</td>
                </tr>
              ))}
              {!data.pincode_performance.length && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
